import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  getWhatsappStatus, pedirAcessoAoWhatsapp, createWhatsappCode, unlinkWhatsapp, getHabit, testWhatsapp,
  WhatsappStatus, RetratoDoHabito, EstadoDoCanal,
} from '../services/api';
import './WhatsappModal.css';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useConfirmar } from '../contexts/Confirmacao';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';

/**
 * Onde o código de pareamento aparece.
 *
 * ── Por que existe este passo ──────────────────────────────────────────────
 *
 * A alternativa era criar conta direto pela conversa do WhatsApp. Como o
 * webhook do Z-API não é assinado, isso significaria que quem descobrisse o
 * número poderia criar conta e gravar dado — uma porta de entrada sem tranca
 * no sistema que guarda a vida financeira dela.
 *
 * Aqui a identidade nasce atrás do login do Google e o WhatsApp só a consome.
 * Custa um passo, uma vez.
 */

interface Props { onClose: () => void; userId?: string }

/*
 * O status e o hábito, guardados por conta (22/10), como Newsletter, Plano e
 * Compartilhar: o modal abre com o que já veio e confere em silêncio; o menu
 * do avatar já busca antes. O código de vínculo nunca é guardado.
 */
type Lido = { s: WhatsappStatus; h: RetratoDoHabito };
const guardados = new Map<string, Lido>();
const buscando = new Map<string, Promise<Lido>>();

export const buscarWhatsapp = (userId = ''): Promise<Lido> => {
  const emCurso = buscando.get(userId);
  if (emCurso) return emCurso;
  const pedido = Promise.all([getWhatsappStatus(), getHabit()])
    .then(([s, h]) => { const r = { s, h }; guardados.set(userId, r); return r; })
    .finally(() => buscando.delete(userId));
  buscando.set(userId, pedido);
  return pedido;
};

const ROTULO_TENDENCIA = {
  subindo: 'está subindo',
  caindo: 'está caindo',
  estavel: 'está estável',
  'sem-dado': 'sem dado ainda',
} as const;

/**
 * "O hábito pegou?" — a pergunta que decide o futuro do bot.
 *
 * Mostra CONSTÂNCIA e não volume, porque 20 lançamentos num sábado de
 * arrumação e 20 espalhados em 14 dias são hábitos opostos com o mesmo número.
 *
 * A comparação entre as duas quinzenas é a régua escrita: na faixa
 * inconclusiva de cobertura (50% a 80%), o desempate é a tendência.
 */
/**
 * O canal está no ar?
 *
 * `linked` e isto são coisas diferentes, e a tela mostrava só a primeira. O
 * vínculo é nosso e fica no banco; a conexão é do Z-API com o WhatsApp. Em
 * 26/08/2026 a instância ficou 4 horas restrita e esta tela continuou dizendo
 * "ligado ao número …2443" o tempo todo — verdade, e inútil.
 *
 * Fora do ar aparece como AVISO e não como rótulo neutro, porque a mensagem
 * que ela mandar nesse estado não dá erro: some.
 */
const SeloDoCanal: React.FC<{ c: EstadoDoCanal }> = ({ c }) => {
  if (c.estado === null) {
    return (
      <p className="wam-canal wam-canal-mudo">
        Ainda não medi se o bot está no ar.
      </p>
    );
  }
  if (c.estado === 'fora-do-ar') {
    return (
      <div className="wam-canal wam-canal-fora">
        <strong>O bot está fora do ar.</strong>
        <span>
          O que você mandar por lá não chega, e não dá erro: some. Reconecte a
          instância no painel do Z-API.
        </span>
      </div>
    );
  }
  return <p className="wam-canal wam-canal-no-ar">O bot está no ar.</p>;
};

const RetratoHabito: React.FC<{ h: RetratoDoHabito }> = ({ h }) => (
  <div className="wam-habito">
    <span className="wam-habito-titulo">O hábito pegou?</span>

    <div className="wam-habito-linha">
      <strong>{h.atual.diasComLancamento}</strong> de {h.atual.totalDias} dias
      com lançamento
      <span className="wam-habito-antes">
        (quinzena anterior: {h.anterior.diasComLancamento})
      </span>
    </div>

    {/* A ressalva vem ANTES da tendência, não como rodapé.
        Um número de hábito lido sem saber que o canal caiu manda a decisão
        para o lado errado: "não pegou" quando na verdade era "estava fora do
        ar". Depois de lida a conclusão, a ressalva chega tarde. */}
    {h.canal.diasForaDoAr > 0 && (
      <div className="wam-habito-ressalva">
        O bot esteve fora do ar em <strong>{h.canal.diasForaDoAr}</strong>{' '}
        {h.canal.diasForaDoAr === 1 ? 'dia' : 'dias'} desta quinzena. Esses dias
        contam contra você aqui sem serem culpa sua.
      </div>
    )}
    {!h.canal.medicaoConfiavel && h.canal.diasForaDoAr === 0 && (
      <div className="wam-habito-ressalva">
        Não sei se o bot esteve no ar em {h.canal.diasSemDado} dos{' '}
        {h.janelaDias} dias, então este número não sustenta decisão ainda.
      </div>
    )}
    <div className={`wam-habito-tendencia ${h.tendencia}`}>
      {ROTULO_TENDENCIA[h.tendencia]}
    </div>

    <div className="wam-habito-detalhe">
      {h.atual.lancamentos} lançamento{h.atual.lancamentos === 1 ? '' : 's'} na
      quinzena: {h.atual.porWhatsapp} pelo WhatsApp, {h.atual.porWeb} pelo app.
      {h.atual.maiorIntervalo > 0 && ` Maior silêncio: ${h.atual.maiorIntervalo} dia${h.atual.maiorIntervalo === 1 ? '' : 's'}.`}
    </div>

    <p className="wam-nota">
      {/*
        Encurtado, sem perder nenhuma das duas coisas que ele diz.

        Eram 190 caracteres e cinco linhas no celular, num modal que já
        estourava a tela. O que NÃO dava para cortar: que o número é sobre
        constância (e não sobre cobertura), e que a cobertura só ela consegue
        medir. Tirar a segunda parte deixaria o número parecendo a resposta
        inteira, que é justamente o que a régua dos 14 dias evita.

        O que saiu foi a explicação de POR QUE o app só vê constância — dá
        para deduzir de "só você consegue conferir".
      */}
      Isto mede <strong>constância</strong>. A cobertura contra a fatura
      (quanto das suas compras chegou aqui) só você consegue conferir.
    </p>
  </div>
);

const WhatsappModal: React.FC<Props> = ({ onClose, userId = '' }) => {
  const jaVeio = guardados.get(userId);
  const confirmar = useConfirmar();
  useTravarScroll();
  /* O voltar do celular fecha isto antes de sair do app. */
  useFechavelComVoltar(true, onClose);
  const caixa = useRef<HTMLDivElement>(null);
  useFocoNoModal(caixa);

  const [status, setStatus] = useState<WhatsappStatus | null>(jaVeio?.s ?? null);
  const [habito, setHabito] = useState<RetratoDoHabito | null>(jaVeio?.h ?? null);
  const [codigo, setCodigo] = useState<string | null>(null);
  const [expiraEm, setExpiraEm] = useState<number | null>(null);
  /** Só existe para forçar o re-render do relógio a cada segundo. */
  const [, tick] = useState(0);
  const [carregando, setCarregando] = useState(!jaVeio);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [testando, setTestando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState<string | null>(null);
  const [pedindo, setPedindo] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const { s, h } = await buscarWhatsapp(userId);
      setStatus(s);
      setHabito(h);
      setErro(null);
    } catch {
      setErro('Não consegui carregar o status.');
    } finally {
      setCarregando(false);
    }
  }, [userId]);

  useEffect(() => { carregar(); }, [carregar]);

  // Esc fecha. Modal que só fecha no X é modal que prende.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A contagem regressiva não é enfeite: sem ela, um código expirado parece
  // válido e ela culpa o bot por recusar.
  //
  // O `tick` existe porque re-setar `expiraEm` com o MESMO valor não
  // re-renderiza — React compara e descarta. Sem um estado que muda de fato,
  // o relógio ficaria parado no primeiro segundo.
  useEffect(() => {
    if (expiraEm == null) return;
    const t = setInterval(() => {
      if (expiraEm - Date.now() <= 0) {
        setCodigo(null);
        setExpiraEm(null);
      } else {
        tick(n => n + 1);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [expiraEm]);

  const gerar = async () => {
    setGerando(true);
    try {
      const r = await createWhatsappCode();
      setCodigo(r.code);
      setExpiraEm(new Date(r.expiresAt).getTime());
      setErro(null);
    } catch {
      setErro('Não consegui gerar o código. Tente de novo.');
    } finally {
      setGerando(false);
    }
  };

  /**
   * O modo de falha mais confuso deste canal é silencioso: a transação é
   * gravada, o número está vinculado, e nada volta. Este botão traz o motivo
   * do servidor para a tela em vez de mandar procurar no log.
   */
  const pedir = async () => {
    setPedindo(true);
    try {
      const acesso = await pedirAcessoAoWhatsapp();
      setStatus(s => (s ? { ...s, acesso } : s));
      setErro(null);
    } catch {
      setErro('Não consegui registrar o pedido. Tente de novo.');
    } finally {
      setPedindo(false);
    }
  };

  const testar = async () => {
    setTestando(true);
    setResultadoTeste(null);
    try {
      const r = await testWhatsapp();
      if (r.ok) {
        setResultadoTeste('✅ Enviado. Confira o WhatsApp: se a mensagem chegou, está tudo certo.');
      } else {
        const explica: Record<string, string> = {
          desligado: 'WHATSAPP_ENABLED não está ligado no servidor.',
          'sem-credencial': 'Falta ZAPI_INSTANCE_ID ou ZAPI_TOKEN no servidor.',
          'runtime-sem-fetch': 'O servidor está num Node antigo demais (precisa de 18+).',
          recusado: `O Z-API recusou (HTTP ${r.status}).`,
          'erro-de-rede': 'Não consegui falar com o Z-API.',
        };
        // O Z-API responde em inglês e técnico. Traduzir os erros que já
        // aconteceram de verdade poupa uma volta inteira de diagnóstico —
        // "your client-token is not configured" não diz que a tela é outra.
        const pistas: [RegExp, string][] = [
          [/client-token/i,
            'Falta o ZAPI_CLIENT_TOKEN. Ele fica no Z-API em '
            + '**menu da conta → Segurança**, e não na tela da instância.'],
          [/token|unauthor|invalid/i,
            'Credencial recusada: confira ZAPI_TOKEN e ZAPI_INSTANCE_ID.'],
          [/disconnect|not connected|desconect/i,
            'A instância está desconectada. Reconecte o chip pelo QR code.'],
        ];
        const pista = pistas.find(([re]) => re.test(r.detalhe ?? ''))?.[1];
        setResultadoTeste(
          `❌ ${explica[r.motivo ?? ''] ?? 'Falhou.'}`
          + (pista ? `\n\n${pista}` : '')
          + (r.detalhe ? `\n\n${r.detalhe}` : ''),
        );
      }
    } catch {
      setResultadoTeste('❌ Não consegui testar agora.');
    } finally {
      setTestando(false);
    }
  };

  const desvincular = async () => {
    if (!await confirmar({
      titulo: 'Desvincular este número?',
      alvo: status?.phoneMasked ?? undefined,
      texto: 'O WhatsApp para de lançar e de avisar. Nenhum dado é apagado, dá para parear de novo quando quiser.',
      acao: 'Desvincular', perigo: true,
    })) return;
    await unlinkWhatsapp();
    setCodigo(null);
    await carregar();
  };

  const restante = expiraEm != null
    ? Math.max(0, Math.ceil((expiraEm - Date.now()) / 1000))
    : null;

  return createPortal(
    <div className="wam-overlay" onClick={onClose} role="presentation" ref={caixa}>
      <div
        className="wam-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wam-titulo"
      >
        {/* A banda existe para dar identidade: tapando a palavra "WhatsApp",
            nada no card dizia de que ele era. O glifo é desenhado, não emoji —
            emoji como elemento de design varia por plataforma e vira ruído. */}
        <div className="wam-banda wam-header">
          <span className="wam-banda-glifo" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2a10 10 0 00-8.5 15.3L2 22l4.8-1.4A10 10 0 1012 2zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3 .9.9-2.9-.2-.3A8.2 8.2 0 1112 20.2z"/>
              <path d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5 0-.2 0-.4-.1-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1.1 2.8 1.2 3c.1.2 2.1 3.2 5 4.5.7.3 1.3.5 1.7.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.5-.3z"/>
            </svg>
          </span>
          <h2 id="wam-titulo">WhatsApp</h2>
          <button className="wam-fechar" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className="wam-corpo">

        {carregando ? (
          <p className="wam-texto">Carregando…</p>
        ) : status?.linked ? (
          <>
            <p className="wam-texto">
              Ligado ao número terminado em <strong>{status.phoneMasked}</strong>.
            </p>
            <p className="wam-nota">
              Escreva o gasto por lá, tipo <em>mercado 187,40 no débito</em>, que ele lança.
              Você também recebe lembretes e alertas de limite no mesmo lugar.
            </p>
            {/* O conteúdo vem primeiro. Antes, o botão de teste — uma
                ferramenta de diagnóstico usada quase nunca — era uma caixa de
                largura inteira ACIMA do painel de hábito, e lia como a ação
                principal da tela. Peso visual seguindo importância, não ordem
                de implementação. */}
            {status.canal && <SeloDoCanal c={status.canal} />}

            {habito && <RetratoHabito h={habito} />}

            {resultadoTeste && <pre className="wam-teste-resultado">{resultadoTeste}</pre>}

            {/* As duas utilidades juntas, na mesma linha e no mesmo peso: uma
                diagnostica, a outra desfaz. Nenhuma é o motivo de abrir isto. */}
            <div className="wam-utilidades">
              <button className="wam-testar" onClick={testar} disabled={testando} aria-busy={testando}>
                Testar envio
              </button>
              <button className="wam-desvincular" onClick={desvincular}>
                Desvincular
              </button>
            </div>
          </>
        ) : codigo ? (
          <>
            <div className="wam-codigo" aria-live="polite">{codigo}</div>
            {restante != null && (
              <p className="wam-contagem">
                Vale por mais {Math.floor(restante / 60)}min {String(restante % 60).padStart(2, '0')}s
              </p>
            )}

            {/* O botão faz o trabalho todo: abre a conversa certa com o código
                já escrito. Pedir para ela decorar seis dígitos e achar o número
                sozinha é onde um pareamento simples vira suporte técnico. */}
            {status?.botPhone ? (
              <a
                className="wam-abrir"
                href={`https://wa.me/${status.botPhone}?text=${encodeURIComponent(codigo)}`}
                target="_blank"
                rel="noreferrer"
              >
                Abrir o WhatsApp com o código
              </a>
            ) : (
              <p className="wam-alerta">
                O servidor não tem o número do bot configurado
                (<code>ZAPI_PHONE</code>), então não consigo te dizer para onde
                mandar. Cadastre a variável no Render.
              </p>
            )}

            <p className="wam-nota">
              Precisa ser do <strong>seu</strong> número, porque é ele que fica
              ligado à conta. O código serve uma vez só e expira sozinho.
            </p>
          </>
        ) : status?.acesso && status.acesso !== 'liberado' ? (
          /*
            O canal a pedido (05/10).

            Conta nova não pareia sozinha: o número do bot é um só, roda por
            conexão não oficial, e cada conta a mais é risco para todas. A
            pessoa pede aqui e a dona libera no painel de saúde.

            A tela diz O QUE o canal faz antes do botão — quem chegou por
            "peça o seu acesso" na página de apresentação precisa saber o que
            está pedindo. E não promete prazo nem aviso: o que existe é esta
            tela mudando quando a liberação acontece.

            O portão de verdade é o servidor, que recusa gerar o código.
          */
          <>
            <p className="wam-texto">
              Pelo WhatsApp você lança um gasto mandando uma mensagem, um áudio
              ou a foto do comprovante, e pergunta quanto ainda pode gastar.
            </p>
            {status.acesso === 'pedido' ? (
              <p className="wam-pedido" role="status">
                <strong>Pedido recebido.</strong> Estamos liberando o acesso aos
                poucos. Quando o seu for liberado, o pareamento aparece aqui.
              </p>
            ) : (
              <>
                <p className="wam-texto">
                  O acesso é liberado aos poucos, para o canal continuar
                  funcionando bem para todo mundo.
                </p>
                <button className="wam-gerar" onClick={pedir} disabled={pedindo} aria-busy={pedindo}>
                  Pedir acesso
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <p className="wam-texto">
              Lançar pelo WhatsApp começa aqui: gere um código e mande no zap.
            </p>

            {/* O que impede o pareamento de funcionar, dito ANTES de ela tentar.
                Sem isto, a mensagem dela é rejeitada com 403 e ela recebe
                silêncio — o motivo fica só no log do servidor.

                O aviso da allowlist morava aqui e saiu em 01/09 junto com ela.
                Ele era pior que inútil: só alarmava com a lista VAZIA, então
                com um número dentro dizia "tudo pronto" — enquanto recusava
                todo mundo, menos aquele um. */}
            {status && !status.serverReady && (
              <p className="wam-alerta">
                O servidor ainda não está configurado para WhatsApp. Faltam as
                variáveis <code>ZAPI_INSTANCE_ID</code>, <code>ZAPI_TOKEN</code> e{' '}
                <code>WHATSAPP_WEBHOOK_SECRET</code> no Render.
              </p>
            )}
            {status?.serverReady && !status.sendingEnabled && (
              <p className="wam-alerta">
                <code>WHATSAPP_ENABLED</code> está desligado: o bot recebe suas
                mensagens mas não responde nada.
              </p>
            )}

            <p className="wam-nota">
              O código nasce nesta tela, atrás do seu login, porque é ele que
              prova que o número é seu. Nenhuma conta é criada pela conversa.
            </p>
            <button className="wam-gerar" onClick={gerar} disabled={gerando} aria-busy={gerando}>
              Gerar código
            </button>
          </>
        )}

        {erro && <p className="wam-erro">{erro}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WhatsappModal;
